import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR } from "@/lib/roles";
import { Search, Plus, Loader2, Home } from "lucide-react";

export default function PropertyList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [ward, setWard] = useState("all");
  const [ptype, setPtype] = useState("all");
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(true);
  const limit = 15;

  useEffect(() => { api.get("/master/wards").then((r) => setWards(r.data.items || [])); }, []);

  const fetchList = () => {
    setLoading(true);
    const params = { page, limit };
    if (q) params.q = q;
    if (ward !== "all") params.ward = ward;
    if (ptype !== "all") params.property_type = ptype;
    api.get("/properties", { params }).then((r) => {
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [page, ward, ptype]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); fetchList(); };
  const canCreate = ["revenue_officer", "data_entry_operator", "tax_inspector", "executive_officer", "municipal_commissioner", "super_admin", "state_admin"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="property-list-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Module</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 dark:text-white">Property Tax</h1>
        </div>
        {canCreate && (
          <Link to="/properties/new">
            <Button className="bg-[#0F2942]" data-testid="btn-new-property"><Plus className="w-4 h-4 mr-1" /> Register Property</Button>
          </Link>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={onSearch} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ID, owner or address" className="pl-9" data-testid="input-property-search" />
            </div>
            <Select value={ward} onValueChange={(v) => { setWard(v); setPage(1); }}>
              <SelectTrigger data-testid="select-ward"><SelectValue placeholder="All wards" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All wards</SelectItem>
                {wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ptype} onValueChange={(v) => { setPtype(v); setPage(1); }}>
              <SelectTrigger data-testid="select-property-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="industrial">Industrial</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No properties found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="property-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-3 px-4">Property ID</th>
                    <th className="text-left py-3 px-4">Owner</th>
                    <th className="text-left py-3 px-4 hidden md:table-cell">Ward</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Type</th>
                    <th className="text-right py-3 px-4">Annual Tax</th>
                    <th className="text-right py-3 px-4">Dues</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-3 px-4 font-mono text-xs">{p.property_id}</td>
                      <td className="py-3 px-4">
                        <div className="font-medium">{p.owner_name}</div>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{p.address}</div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell">{p.ward}</td>
                      <td className="py-3 px-4 hidden lg:table-cell capitalize"><Badge variant="outline">{p.property_type}</Badge></td>
                      <td className="py-3 px-4 text-right font-mono">{formatINR(p.annual_tax)}</td>
                      <td className="py-3 px-4 text-right font-mono">
                        {p.total_dues > 0 ? (
                          <span className="text-red-600 font-semibold">{formatINR(p.total_dues)}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Link to={`/properties/${p.id}`}>
                          <Button size="sm" variant="ghost" data-testid={`btn-view-${p.property_id}`}>View</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="btn-prev-page">Previous</Button>
          <Button size="sm" variant="outline" disabled={page * limit >= total} onClick={() => setPage(page + 1)} data-testid="btn-next-page">Next</Button>
        </div>
      </div>
    </div>
  );
}
